import * as THREE from 'three'
import {
  LineBVH,
  LineLoopBVH,
  LineSegmentsBVH,
  MeshBVH,
  PointsBVH,
  acceleratedRaycast,
  computeBoundsTree,
  disposeBoundsTree,
  type ComputeBVHOptions,
  type GeometryBVH,
} from 'three-mesh-bvh'

/**
 * 带有 BVH 扩展字段的几何体。
 *
 * 设计说明：
 * 1. `three-mesh-bvh` 只对 `three` 模块做了类型扩展，而项目同时存在 `three` / `three/webgpu` 两套导入；
 * 2. 这里在本地显式声明最小可用形状，避免把库的类型耦合扩散到业务模块；
 * 3. 业务层只需要调用“构建对象树 BVH”能力，不需要感知 `boundsTree` 等底层细节。
 */
type BvhReadyBufferGeometry = THREE.BufferGeometry & {
  boundsTree?: GeometryBVH
}

/**
 * BVH 实例的 indirect 扩展字段。
 *
 * 设计说明：
 * 1. `three-mesh-bvh` 会在运行时记录 indirect 模式，但当前类型定义没有公开该字段；
 * 2. 本地收口这个最小扩展，避免业务模块到处散落 `any` 断言；
 * 3. 该字段只用于幂等判断，不参与 BVH 的构建或面索引映射。
 */
type GeometryBvhWithIndirect = GeometryBVH & {
  indirect?: boolean
}

/**
 * BVH 类型别名。
 */
type RaycastAccelerationType = NonNullable<ComputeBVHOptions['type']>

/**
 * 射线加速配置。
 *
 * 设计说明：
 * 1. `three-mesh-bvh` 的 direct 模式会原地重排 `geometry.index`；
 * 2. 对依赖稳定三角形索引语义的 CAD 网格，必须改用 `indirect=true`；
 * 3. 这样 BVH 仍可加速命中，但不会破坏上层的 triangle -> face/body/primitive 映射契约。
 */
type RaycastAccelerationConfig = {
  type: RaycastAccelerationType
  indirect: boolean
}

/**
 * 已接入过加速射线的对象集合。
 *
 * 设计说明：
 * 1. 这里用于区分“业务自己显式覆写的 raycast”与“BVH 模块安装的 raycast”；
 * 2. 如果对象本身就是 `raycast = noop`，必须保留原行为，不能被 BVH 覆盖；
 * 3. 用 `WeakSet` 持有运行时标记，避免污染 `userData` 或引入额外释放负担。
 */
const acceleratedObjectSet = new WeakSet<THREE.Object3D>()

/**
 * 已绑定 dispose 清理监听器的几何体集合。
 *
 * 设计说明：
 * 1. BVH 构建后会把树结构挂在 `geometry.boundsTree` 上；
 * 2. 几何体真正 `dispose()` 时，需要同步清空这份结构，避免长生命周期对象残留大块 JS 内存；
 * 3. 使用 `WeakSet` 保证每个 geometry 只绑定一次监听器。
 */
const observedGeometrySet = new WeakSet<THREE.BufferGeometry>()

/**
 * 可接入 BVH 的对象类型。
 */
type AccelerableObject3D =
  | THREE.Mesh
  | THREE.Points
  | THREE.Line
  | THREE.LineLoop
  | THREE.LineSegments

/**
 * 解析对象对应的默认 raycast 实现。
 *
 * @param object 目标对象。
 * @returns 默认 raycast；对象不支持时返回 `null`。
 */
function resolveDefaultRaycast(
  object: THREE.Object3D,
): THREE.Object3D['raycast'] | null {
  if ((object as THREE.LineSegments).isLineSegments === true) {
    return THREE.LineSegments.prototype.raycast
  }
  if ((object as THREE.LineLoop).isLineLoop === true) {
    return THREE.LineLoop.prototype.raycast
  }
  if ((object as THREE.Line).isLine === true) {
    return THREE.Line.prototype.raycast
  }
  if ((object as THREE.Points).isPoints === true) {
    return THREE.Points.prototype.raycast
  }
  if ((object as THREE.Mesh).isMesh === true) {
    return THREE.Mesh.prototype.raycast
  }
  return null
}

/**
 * 判断对象是否为可加速的射线对象。
 *
 * @param object 目标对象。
 * @returns 是否可接入 BVH。
 */
function isAccelerableObject(object: THREE.Object3D): object is AccelerableObject3D {
  return (
    (object as THREE.Mesh).isMesh === true
    || (object as THREE.Points).isPoints === true
    || (object as THREE.Line).isLine === true
  )
}

/**
 * 判断几何体是否具备可参与射线加速的顶点数据。
 *
 * 设计说明：
 * 1. 测试桩、占位对象或延迟填充的导入节点可能会携带空 BufferGeometry；
 * 2. `three-mesh-bvh` 在缺少 position attribute 时会直接抛错，因此必须在统一入口跳过；
 * 3. 该判断只影响 BVH 构建，不改变对象原本的可见性、层级或资源生命周期。
 *
 * @param geometry 待检查的几何体。
 * @returns 是否具备可构建 BVH 的 position 顶点数据。
 */
function hasRaycastablePositionAttribute(geometry: THREE.BufferGeometry): boolean {
  const positionAttribute = geometry.getAttribute('position')
  return typeof positionAttribute?.count === 'number' && positionAttribute.count > 0
}

/**
 * 销毁指定几何体上的 BVH。
 *
 * 设计说明：
 * 1. 该函数只处理 `boundsTree` 生命周期，不负责几何体本身的释放；
 * 2. 这样上层在替换 geometry、重建 geometry 或手动 dispose 前，都能显式先清理旧 BVH；
 * 3. 若几何体尚未构建 BVH，则保持幂等，不抛出错误。
 *
 * @param geometry 目标几何体。
 */
export function disposeRaycastAccelerationForGeometry(
  geometry: THREE.BufferGeometry,
): void {
  const bvhReadyGeometry = geometry as BvhReadyBufferGeometry
  if (!bvhReadyGeometry.boundsTree) {
    return
  }

  disposeBoundsTree.call(bvhReadyGeometry)
}

/**
 * 为几何体生成 BVH。
 *
 * 设计说明：
 * 1. 该函数只负责底层几何缓存，不直接干预对象实例的 raycast；
 * 2. 导入、缓存预热与几何更新链路都可以复用这份实现；
 * 3. 当目标几何体已经存在同类型 BVH 时保持幂等，避免重复构建。
 *
 * @param geometry 目标几何体。
 * @param config
 */
export function createRaycastAccelerationForGeometry(
  geometry: THREE.BufferGeometry,
  config: RaycastAccelerationConfig,
): void {
  const bvhReadyGeometry = geometry as BvhReadyBufferGeometry
  const currentTree = bvhReadyGeometry.boundsTree
  const hasMatchedType = currentTree instanceof config.type
  const hasMatchedIndirectMode = hasMatchedType
    && Boolean((currentTree as GeometryBvhWithIndirect).indirect) === config.indirect
  if (!hasMatchedIndirectMode) {
    computeBoundsTree.call(bvhReadyGeometry, {
      type: config.type,
      indirect: config.indirect,
    })
  }
  observeGeometryDispose(bvhReadyGeometry)
}

/**
 * 更新几何体上的 BVH。
 *
 * 设计说明：
 * 1. 顶点、索引或拓扑结构变化后，旧 BVH 已经失效；
 * 2. 调用方不应自己拼装“先销毁再重建”的顺序；
 * 3. 对象树级更新会在更高层负责去重，这里只处理单个几何体。
 *
 * @param geometry 目标几何体。
 * @param config
 */
export function updateRaycastAccelerationForGeometry(
  geometry: THREE.BufferGeometry,
  config: RaycastAccelerationConfig,
): void {
  disposeRaycastAccelerationForGeometry(geometry)
  createRaycastAccelerationForGeometry(geometry, config)
}

/**
 * 在几何拓扑不变、仅顶点坐标变化时原位校正既有 BVH。
 *
 * 设计说明：
 * 1. `refit()` 保留现有 BVH 实例与分区拓扑，只根据最新顶点坐标重算包围体；
 * 2. 该入口不会为尚未接入射线加速的几何体新建 BVH，避免构造阶段产生额外开销；
 * 3. 顶点数量、索引或图元拓扑发生变化时不得调用本函数，应改用完整更新入口。
 *
 * @param geometry 仅顶点坐标发生变化的目标几何体。
 */
export function refitRaycastAccelerationForGeometry(
  geometry: THREE.BufferGeometry,
): void {
  const currentTree = (geometry as BvhReadyBufferGeometry).boundsTree
  if (!currentTree) {
    return
  }

  currentTree.refit()
}

/**
 * 销毁对象树上所有可加速几何体的 BVH。
 *
 * 设计说明：
 * 1. 该函数用于对象树整体释放场景；
 * 2. 共享几何只销毁一次，避免重复操作同一棵树；
 * 3. 与几何级销毁函数配合后，BVH 生命周期具备明确的工具出口。
 *
 * @param root 目标对象树根节点。
 * @returns 原对象，便于链式复用。
 */
export function disposeRaycastAccelerationForObjectTree<TObject extends THREE.Object3D>(
  root: TObject,
): TObject {
  const disposedGeometrySet = new Set<THREE.BufferGeometry>()

  root.traverse((object) => {
    if (!isAccelerableObject(object)) {
      return
    }

    const geometry = object.geometry
    if (!(geometry instanceof THREE.BufferGeometry) || disposedGeometrySet.has(geometry)) {
      return
    }

    disposeRaycastAccelerationForGeometry(geometry)
    disposedGeometrySet.add(geometry)
  })

  return root
}

/**
 * 解析对象应使用的 BVH 类型。
 *
 * @param object 目标对象。
 * @returns BVH 构造器；对象不支持时返回 `null`。
 */
function resolveBvhType(
  object: AccelerableObject3D,
): RaycastAccelerationType | null {
  if ((object as THREE.LineSegments).isLineSegments === true) {
    return LineSegmentsBVH
  }
  if ((object as THREE.LineLoop).isLineLoop === true) {
    return LineLoopBVH
  }
  if ((object as THREE.Line).isLine === true && (object as THREE.Mesh).isMesh !== true) {
    return LineBVH
  }
  if ((object as THREE.Points).isPoints === true) {
    return PointsBVH
  }
  if ((object as THREE.Mesh).isMesh === true) {
    return MeshBVH
  }
  return null
}

/**
 * 判断当前 mesh 是否依赖稳定的三角形索引语义。
 *
 * 设计说明：
 * 1. direct BVH 会改写 `geometry.index`，从而改变 `Intersection.faceIndex` 对应的三角形编号；
 * 2. 只要上层把三角形编号映射到面、body 或 primitive，这种重排就会直接破坏选择正确性；
 * 3. 因此这里统一识别这类 mesh，并让它们走 `indirect BVH`。
 *
 * @param object 目标对象。
 * @returns 是否必须保留原始三角形顺序。
 */
function requiresStableTriangleOrderForRaycast(object: AccelerableObject3D): boolean {
  if ((object as THREE.Mesh).isMesh !== true) {
    return false
  }

  const topologyAwareObject = object as THREE.Object3D & {
    getFeatureFaceByTriangleIndex?: unknown
  }
  if (typeof topologyAwareObject.getFeatureFaceByTriangleIndex === 'function') {
    return true
  }

  const userData = object.userData as {
    faceTriangleRanges?: unknown
    partAssetLocal?: {
      primitiveFaceRanges?: unknown
    }
  } | undefined

  if (Array.isArray(userData?.faceTriangleRanges)) {
    return true
  }

  if (Array.isArray(userData?.partAssetLocal?.primitiveFaceRanges)) {
    return true
  }

  return false
}

/**
 * 解析对象应使用的射线加速配置。
 *
 * @param object 目标对象。
 * @returns 加速配置；对象不支持时返回 `null`。
 */
function resolveRaycastAccelerationConfig(
  object: AccelerableObject3D,
): RaycastAccelerationConfig | null {
  const type = resolveBvhType(object)
  if (!type) {
    return null
  }

  return {
    type,
    indirect: requiresStableTriangleOrderForRaycast(object),
  }
}

/**
 * 合并共享几何体的射线加速配置。
 *
 * 设计说明：
 * 1. 同一份 `BufferGeometry` 可能被多个实例共享；
 * 2. 只要其中任意一个实例依赖稳定三角顺序，这份几何就必须整体退到 `indirect BVH`；
 * 3. 这样可以避免“先被 direct 模式构建，再被语义 mesh 复用”导致的时序型错误。
 *
 * @param current 已收集配置。
 * @param next 新配置。
 * @returns 合并后的配置。
 */
function mergeRaycastAccelerationConfig(
  current: RaycastAccelerationConfig | undefined,
  next: RaycastAccelerationConfig,
): RaycastAccelerationConfig {
  if (!current) {
    return next
  }

  return {
    type: current.type,
    indirect: current.indirect || next.indirect,
  }
}

/**
 * 判断对象是否显式覆写了 raycast。
 *
 * @param object 目标对象。
 * @returns 是否应保留现有 raycast。
 */
function shouldPreserveCurrentRaycast(object: AccelerableObject3D): boolean {
  if (acceleratedObjectSet.has(object)) {
    return false
  }

  const defaultRaycast = resolveDefaultRaycast(object)
  if (!defaultRaycast) {
    return true
  }

  return object.raycast !== defaultRaycast
}

/**
 * 给几何体绑定 dispose 时的 BVH 清理。
 *
 * @param geometry 几何体。
 */
function observeGeometryDispose(geometry: BvhReadyBufferGeometry): void {
  if (observedGeometrySet.has(geometry)) {
    return
  }

  observedGeometrySet.add(geometry)
  geometry.addEventListener('dispose', () => {
    if (geometry.boundsTree) {
      disposeBoundsTree.call(geometry)
    }
    observedGeometrySet.delete(geometry)
  })
}

/**
 * 为单个对象安装 BVH 射线加速。
 *
 * @param object 目标对象。
 */
function installRaycastAccelerationForObject(object: AccelerableObject3D): void {
  if (shouldPreserveCurrentRaycast(object)) {
    return
  }

  const geometry = object.geometry as BvhReadyBufferGeometry | undefined
  if (!geometry || (geometry as THREE.BufferGeometry).isBufferGeometry !== true) {
    return
  }

  if (!hasRaycastablePositionAttribute(geometry)) {
    return
  }

  object.raycast = acceleratedRaycast as THREE.Object3D['raycast']
  acceleratedObjectSet.add(object)
}

/**
 * 为对象树内所有可射线命中的对象构建 BVH。
 *
 * 设计说明：
 * 1. 该函数只关心“对象树具备高性能 raycast 能力”，不关心对象最终会被挂到哪个父节点；
 * 2. 共享 `BufferGeometry` 时，`boundsTree` 只会构建一次，后续实例只复用树结构与加速 raycast；
 * 3. 对显式覆写了 `raycast` 的对象保持原样，避免把不可选辅助对象意外变成可选对象。
 *
 * @param root 待处理的对象树根节点。
 * @returns 原对象，便于链式复用。
 */
export function ensureRaycastAccelerationForObjectTree<TObject extends THREE.Object3D>(
  root: TObject,
): TObject {
  const geometryConfigMap = new Map<THREE.BufferGeometry, RaycastAccelerationConfig>()

  root.traverse((object) => {
    if (!isAccelerableObject(object)) {
      return
    }

    if (shouldPreserveCurrentRaycast(object)) {
      return
    }

    const accelerationConfig = resolveRaycastAccelerationConfig(object)
    if (!accelerationConfig) {
      return
    }

    const geometry = object.geometry
    if (!(geometry instanceof THREE.BufferGeometry) || !hasRaycastablePositionAttribute(geometry)) {
      return
    }

    geometryConfigMap.set(
      geometry,
      mergeRaycastAccelerationConfig(geometryConfigMap.get(geometry), accelerationConfig),
    )
  })

  geometryConfigMap.forEach((config, geometry) => {
    createRaycastAccelerationForGeometry(geometry, config)
  })

  root.traverse((object) => {
    if (!isAccelerableObject(object)) {
      return
    }

    installRaycastAccelerationForObject(object)
  })

  return root
}

/**
 * 更新对象树上的 BVH。
 *
 * 设计说明：
 * 1. 当对象树内的可拾取几何已经发生顶点、索引或拓扑变化时，应先清理旧 BVH 再重建；
 * 2. 共享 geometry 时只清理一次，避免重复销毁同一棵树；
 * 3. 重建阶段继续复用 `ensureRaycastAccelerationForObjectTree` 的安装逻辑，保证更新后 raycast 行为与首次挂载一致。
 *
 * @param root 待更新的对象树根节点。
 * @returns 原对象，便于链式复用。
 */
export function updateRaycastAccelerationForObjectTree<TObject extends THREE.Object3D>(
  root: TObject,
): TObject {
  const geometryConfigMap = new Map<THREE.BufferGeometry, RaycastAccelerationConfig>()

  root.traverse((object) => {
    if (!isAccelerableObject(object)) {
      return
    }

    if (shouldPreserveCurrentRaycast(object)) {
      return
    }

    const accelerationConfig = resolveRaycastAccelerationConfig(object)
    if (!accelerationConfig) {
      return
    }

    const geometry = object.geometry
    if (!(geometry instanceof THREE.BufferGeometry) || !hasRaycastablePositionAttribute(geometry)) {
      return
    }

    geometryConfigMap.set(
      geometry,
      mergeRaycastAccelerationConfig(geometryConfigMap.get(geometry), accelerationConfig),
    )
  })

  geometryConfigMap.forEach((config, geometry) => {
    updateRaycastAccelerationForGeometry(geometry, config)
  })

  return ensureRaycastAccelerationForObjectTree(root)
}
